import { useState } from "react";
import { Upload, FileText, AlertCircle, CheckCircle, Loader2 } from "lucide-react";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Badge } from "./ui/badge";
import { useToast } from "../hooks/use-toast";
import { aiService } from "../services/ai-service";

interface UnderwritingDocumentIngestProps {
  onDocumentIngested?: (result: any) => void;
  children?: React.ReactNode;
}

export const UnderwritingDocumentIngest = ({ 
  onDocumentIngested,
  children 
}: UnderwritingDocumentIngestProps) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    url: "",
    carrier_slug: "",
    eff_date: "",
    version: "1.0",
    state_scope: ""
  });
  const [result, setResult] = useState<any>(null);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.url.trim()) {
      toast({
        title: "URL Required",
        description: "Please provide a document URL to ingest.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await aiService.ingestUnderwritingDocument({
        url: formData.url.trim(),
        carrier_slug: formData.carrier_slug || undefined,
        eff_date: formData.eff_date || undefined,
        version: formData.version,
        state_scope: formData.state_scope ? formData.state_scope.split(',').map(s => s.trim()) : undefined
      });

      setResult(response);
      
      // Calculate overall security score
      const securityVerification = response.data.security_verification;
      const overallScore = securityVerification.layer5_final?.confidence_score || 0;
      const scorePercentage = (overallScore * 100).toFixed(1);
      
      toast({
        title: "Document Ingested Successfully",
        description: `Document processed with 5-layer security verification. Security Score: ${scorePercentage}%`,
        variant: "default"
      });

      if (onDocumentIngested) {
        onDocumentIngested(response);
      }
    } catch (error) {

      toast({
        title: "Ingestion Failed",
        description: error instanceof Error ? error.message : "Failed to ingest document",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      url: "",
      carrier_slug: "",
      eff_date: "",
      version: "1.0",
      state_scope: ""
    });
    setResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button variant="outline" size="sm">
            <Upload className="h-4 w-4 mr-2" />
            Ingest Document
          </Button>
        )}
      </DialogTrigger>
      <ResponsiveDialogContent size="md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Ingest Underwriting Document
          </DialogTitle>
          <DialogDescription>
            Upload underwriting guidelines or policy documents for AI analysis. 
            Documents will be processed with 5-layer security verification.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="url">Document URL *</Label>
            <Input
              id="url"
              type="url"
              placeholder="https://carrier.com/underwriting-guide.pdf"
              value={formData.url}
              onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="carrier_slug">Carrier Slug (optional)</Label>
            <Input
              id="carrier_slug"
              placeholder="state-farm, allstate, etc."
              value={formData.carrier_slug}
              onChange={(e) => setFormData(prev => ({ ...prev, carrier_slug: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to auto-generate from URL
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="eff_date">Effective Date</Label>
              <Input
                id="eff_date"
                type="date"
                value={formData.eff_date}
                onChange={(e) => setFormData(prev => ({ ...prev, eff_date: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="version">Version</Label>
              <Input
                id="version"
                placeholder="1.0"
                value={formData.version}
                onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="state_scope">State Scope (optional)</Label>
            <Input
              id="state_scope"
              placeholder="CA, NY, TX (comma-separated)"
              value={formData.state_scope}
              onChange={(e) => setFormData(prev => ({ ...prev, state_scope: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty for all states
            </p>
          </div>

          {result && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div className="space-y-2">
                  <h4 className="font-medium text-green-900">Document Successfully Processed</h4>
                  <div className="space-y-1 text-sm text-green-700">
                    <p><strong>Doc Key:</strong> {result.data.doc_key}</p>
                    <p><strong>Carrier:</strong> {result.data.carrier_slug}</p>
                    <p><strong>Status:</strong> {result.data.status}</p>
                    <p><strong>Text Length:</strong> {result.data.document_stats.text_length.toLocaleString()} characters</p>
                    <p><strong>Word Count:</strong> {result.data.document_stats.word_count.toLocaleString()} words</p>
                    <p><strong>Extraction Method:</strong> {result.data.document_stats.extraction_method}</p>
                    
                    {result.data.security_verification && (
                      <div className="mt-3">
                        <p className="font-medium mb-2">5-Layer Security Verification:</p>
                        <div className="grid grid-cols-1 gap-2">
                          {Object.entries(result.data.security_verification).map(([layer, data]: [string, any]) => {
                            const score = (data.confidence_score * 100).toFixed(1);
                            const statusColor = data.status === 'approved' || data.status === 'current' || data.status === 'valid' 
                              ? 'bg-green-100 text-green-800' 
                              : data.status === 'conditional' || data.status === 'requires-review'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800';
                            
                            return (
                              <div key={layer} className={`p-2 rounded text-xs ${statusColor}`}>
                                <div className="flex justify-between items-center">
                                  <span className="font-medium">{layer.replace('layer', 'Layer ').replace('_', ' ')}</span>
                                  <span className="font-bold">{score}%</span>
                                </div>
                                <div className="text-xs opacity-75 mt-1">
                                  Status: {data.status}
                                  {data.alerts && data.alerts.length > 0 && (
                                    <div className="mt-1">
                                      {data.alerts.map((alert: string, idx: number) => (
                                        <div key={idx} className="text-xs opacity-75">• {alert}</div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                          <strong>Overall Security Score:</strong> {((result.data.security_verification.layer5_final?.confidence_score || 0) * 100).toFixed(1)}%
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Ingest Document
                </>
              )}
            </Button>
          </div>
        </form>
      </ResponsiveDialogContent>
    </Dialog>
  );
};
